/**
 * ETAPA VISUAL 5 — PALETA ÚNICA DE MEDALHA
 *
 * O sistema tinha DUAS paletas para a mesma ideia:
 *
 *   ranking público  ->  #D4AF37 / #C0C0C0 / #CD7F32   (identidade e-Torneios)
 *   telas de gestão  ->  text-yellow-500 / text-gray-400 / text-amber-700 /
 *                        text-amber-600                (paleta padrão do Tailwind)
 *
 * A da gestão funcionava no tema escuro, mas o tema claro só sobrescreve hex
 * arbitrário — não sobrescreve `text-yellow-500` nem `text-gray-400`. Resultado:
 * no claro, o ouro das medalhas ficava em ~1,6:1 sobre branco.
 *
 * Aqui existe UMA paleta, e ela é MEDIDA, não escolhida no olho. Contrastes
 * WCAG calculados (limite AA para texto normal = 4,5:1):
 *
 *                      escuro (#141414)   claro (#FFFFFF)
 *   ouro      #D4AF37       8,76:1          -> #8A6D0F   4,91:1
 *   prata     #C0C0C0      10,13:1          -> #6B7280   4,83:1
 *   bronze    #CD7F32       5,86:1          -> #9A5B1F   5,40:1
 *   2º bronze #E0A030       8,11:1          -> #8A6212   5,47:1
 *
 * O tema escuro usa a cor de identidade pura (todas passam com folga). O tema
 * claro usa variantes escurecidas da MESMA família, também aprovadas.
 *
 * `campo` é o nome que a API usa (`medals.gold`, `medals.second_bronze`); a
 * tradução mora só aqui, para nenhuma tela precisar saber dos dois idiomas.
 */

export const MEDALHAS = {
  ouro: {
    rotulo: 'Ouro',
    campo: 'gold',
    classe: 'medalha-ouro',
    fundo: 'bg-[#D4AF37]',
  },
  prata: {
    rotulo: 'Prata',
    campo: 'silver',
    classe: 'medalha-prata',
    fundo: 'bg-[#C0C0C0]',
  },
  bronze: {
    rotulo: 'Bronze',
    campo: 'bronze',
    classe: 'medalha-bronze',
    fundo: 'bg-[#CD7F32]',
  },
  segundoBronze: {
    rotulo: '2º Bronze',
    campo: 'second_bronze',
    classe: 'medalha-segundo-bronze',
    fundo: 'bg-[#E0A030]',
  },
};

/** Ordem canônica — a MESMA em toda tabela, cabeçalho e resumo. */
export const ORDEM_MEDALHAS = ['ouro', 'prata', 'bronze', 'segundoBronze'];

/** Cor de medalha por chave, com queda segura para o bronze. */
export const medalha = (chave) => MEDALHAS[chave] ?? MEDALHAS.bronze;

/**
 * Pódio (1º/2º/3º) — o selo é FUNDO colorido com texto preto, como o ranking
 * público já fazia. Nesse formato o contraste é do preto sobre a cor (8,76:1 no
 * ouro, 10,13:1 na prata, 5,86:1 no bronze), então passa nos dois temas sem
 * precisar de variante clara. Fora do pódio, o selo é neutro.
 */
const PODIO = {
  1: { classe: 'selo-posicao--1', rotulo: '1º' },
  2: { classe: 'selo-posicao--2', rotulo: '2º' },
  3: { classe: 'selo-posicao--3', rotulo: '3º' },
};

export const podio = (posicao) =>
  PODIO[posicao] ?? { classe: 'selo-posicao--neutro', rotulo: `${posicao}º` };

/**
 * Medalhas de uma linha, na ordem canônica, sem os zeros e já com o valor lido
 * do nome de campo da API.
 *
 * `incluirSegundoBronze` existe porque o 4º ícone só faz sentido quando o
 * campeonato tem o 2º Bronze LIGADO — regra que o ranking público já aplica.
 * Sem isso, o template do grid ganharia uma coluna que nunca teria conteúdo.
 */
export function medalhasVisiveis(contagem, incluirSegundoBronze = false) {
  const c = contagem || {};
  const chaves = incluirSegundoBronze ? ORDEM_MEDALHAS : ORDEM_MEDALHAS.slice(0, 3);
  return chaves
    .map((chave) => {
      const def = MEDALHAS[chave];
      return { chave, ...def, valor: Number(c[def.campo]) || 0 };
    })
    .filter((m) => m.valor > 0);
}
